import type {
  AccountFieldV1,
  AccountV1,
  CapacityModelV1,
  CapacityModelRoleRampAndOutputV1,
  AccountFieldMapV1,
  TerritoryAccountV1,
  JobV1,
  PersonFieldV1,
  PersonV1,
  PricingModelOfferingV1,
  PricingModelV1,
  RoleV1,
  ScoringModelScoreV1,
  ScoringModelV1,
  ScoringModelWeightedTamV1,
  WorkspaceV1,
  UserV1,
  WorkspaceUserGroupV1,
  BillingPlanV1,
  BillingPaymentV1,
  CreditLogV1,
  CapacityModelFunnelStageV1,
  ListV1,
  AccountListV1,
  ConnectorV1,
  TerritoryModelV1,
  TerritoryV1
} from "@territoriesai/types";

import { useSessionStore } from "@/stores/session.store";

const sample = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)]!;

const sampleSize = <T>(arr: T[], n: number): T[] =>
  [...arr].sort(() => Math.random() - 0.5).slice(0, n);

const generateRandomTimestamp = () => {
  return new Date(
    // random year between 2010 and 2024
    2010 + Math.floor(Math.random() * 15),
    Math.floor(Math.random() * 10),
    Math.floor(Math.random() * 28)
  ).getTime();
};

export const createMockSystemPersonFields = (): PersonFieldV1[] => {
  const workspaceId = "mock-workspace-id";
  return [
    {
      id: crypto.randomUUID(),
      name: "employeeId",
      type: "TEXT",
      sampleValues: ["123456", "654321", "987654"],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      id: crypto.randomUUID(),
      name: "name",
      type: "TEXT",
      sampleValues: ["John Doe", "Jane Doe", "John Smith"],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      id: crypto.randomUUID(),
      name: "roleName",
      type: "TEXT",
      sampleValues: ["Sales", "Marketing", "Engineering"],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      id: crypto.randomUUID(),
      name: "managerName",
      type: "TEXT",
      sampleValues: ["Adam Kling", "James Wilson", "Courtney Wilson"],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      id: crypto.randomUUID(),
      name: "department",
      type: "TEXT",
      sampleValues: ["Sales", "Marketing", "Engineering"],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      id: crypto.randomUUID(),
      name: "location",
      type: "TEXT",
      sampleValues: ["New York", "San Francisco", "Seattle"],
      isSystem: true,
      workspaceId: workspaceId
    }
  ];
};

export const createMockSystemAccountFields = (): AccountFieldV1[] => {
  const workspaceId = "mock-workspace-id";
  return [
    {
      name: "Account Name",
      type: "TEXT",
      sampleValues: [
        "Brookdale Senior Living, Inc.",
        "Brookdale Senior Living",
        "Brookdale"
      ],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      name: "Account ID",
      type: "TEXT",
      sampleValues: ["22251172158", "0015000000iScJcAAK", "5d8d94d74f6"],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      name: "Account Type",
      type: "TEXT",
      sampleValues: ["Customer", "Prospect", "Vendor"],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      name: "Account Status",
      type: "TEXT",
      sampleValues: ["Opportunity in Progress", "Customer", "Prospect"],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      name: "Industry",
      type: "TEXT",
      sampleValues: ["Finance", "Finance", "Finance"],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      name: "Annual Revenue",
      type: "CURRENCY",
      sampleValues: [1000000000, 1000000, 1000],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      name: "Number Of Employees",
      type: "NUMBER",
      sampleValues: [47000, 10000],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      name: "Account Owner Name",
      type: "TEXT",
      sampleValues: ["Adam Kling", "James Wilson", "Revenue Operations"],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      name: "Address Line 1",
      type: "TEXT",
      sampleValues: [
        "123 East James Avenue",
        "123 E James Ave",
        "123 James Ave"
      ],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      name: "Address Line 2",
      type: "TEXT",
      sampleValues: ["Suite 123", "Building B", "Floor 7"],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      name: "City",
      type: "TEXT",
      sampleValues: ["Eagle Mountain", "Los Angeles", "Chicago"],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      name: "State",
      type: "TEXT",
      sampleValues: ["Utah", "California", "CA"],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      name: "Zip",
      type: "TEXT",
      sampleValues: ["84005", "84005-2001", "84005 - 2001"],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      name: "Country",
      type: "TEXT",
      sampleValues: ["USA", "United States", "United States of America"],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      name: "Latitude",
      type: "NUMBER",
      sampleValues: [40.7128],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      name: "Longitude",
      type: "NUMBER",
      sampleValues: [-74.006],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      name: "Phone",
      type: "TEXT",
      sampleValues: ["8012287847", "(801) 228-7847", "801.228.7847"],
      isSystem: true,
      workspaceId: workspaceId
    },
    {
      name: "Website",
      type: "TEXT",
      sampleValues: ["https://orbytal.ai/", "www.orbytal.ai", "orbytal.ai"],
      isSystem: true,
      workspaceId: workspaceId
    }
  ];
};

export const createRandomAccountField = (config: {
  name?: string;
  isSystem: boolean;
}): AccountFieldV1 => {
  const types = ["CURRENCY", "TEXT", "NUMBER", "BOOLEAN"];
  const sampleValues = [
    [10000000, 1200300, 85698],
    ["Example 1", "Example 2", "Example 3"],
    [100, 2000, 30000],
    [true, false]
  ];
  const index = Math.floor(Math.random() * types.length);
  return {
    workspaceId: "1",
    name: config.name || crypto.randomUUID().split("-")[0]!,
    isSystem: config.isSystem,
    type: types[index]! as AccountFieldV1["type"],
    sampleValues: sampleValues[index]!,
    averageValue: Math.round(Math.random() * 100) / 100
  };
};

export const createXRandomAccountFields = (config: {
  totalCount: number;
  includeSystemFields: boolean;
  fieldNames?: string[];
}): AccountFieldV1[] => {
  const systemFields = createMockSystemAccountFields();
  const customFields = Array.from(Array(config.totalCount)).map((_, i) =>
    createRandomAccountField({ isSystem: false, name: config.fieldNames?.[i] })
  );
  if (config.includeSystemFields) return systemFields.concat(customFields);
  return customFields;
};

export const createRandomFileMappingField = (config: {
  offset: number;
  name?: string;
}): AccountFieldMapV1 => {
  const types: AccountFieldMapV1["type"][] = [
    "CURRENCY",
    "TEXT",
    "NUMBER",
    "BOOLEAN"
  ];
  const sampleValues = [
    [10000000, 1200300, 85698],
    ["Example 1", "Example 2", "Example 3"],
    [100, 2000, 30000],
    [true, false]
  ];
  const index = Math.floor(Math.random() * types.length);
  const name = config.name || crypto.randomUUID().split("-")[0]!;
  return {
    name: name,
    originalName: name,
    ignore: Math.random() < 0.5,
    columnOffset: config.offset,
    type: types[index]!,
    sampleValues: sampleValues[index]!
  };
};

export const createXRandomSystemFileMappingFields = (config: {
  totalCount: number;
}): AccountFieldMapV1[] => {
  const systemFields = createMockSystemAccountFields();

  const extractRandomSystemField = () => {
    const index = Math.floor(Math.random() * systemFields.length);
    const field = systemFields[index];
    systemFields.splice(index, 1);
    return field;
  };

  return Array.from(Array(config.totalCount)).map((_, i) =>
    createRandomFileMappingField({
      offset: i,
      name: extractRandomSystemField()!.name
    })
  );
};

export const createXRandomFileMappingFields = (config: {
  totalCount: number;
  fieldNames?: string[];
  systemFields?: number;
}): AccountFieldMapV1[] => {
  const systemFields = createXRandomSystemFileMappingFields({
    totalCount: config.systemFields || 0
  });
  const suggestedFields = Array.from(Array(config.totalCount)).map((_, i) =>
    createRandomFileMappingField({ offset: i, name: config.fieldNames?.[i] })
  );

  return systemFields.concat(suggestedFields);
};

export const createRandomScoringDetails = () => {
  const tam = Math.floor(Math.random() * (5000000 - 500000 + 1)) + 500000;
  const totalAccounts = Math.floor(Math.random() * (50000 - 10000 + 1)) + 10000;
  const accountsIncluded = Math.floor(Math.random() * (totalAccounts + 1));
  const accountsExcluded = totalAccounts - accountsIncluded;

  return {
    id: crypto.randomUUID().split("-")[0]!,
    name: `Scoring ${new Date().toISOString().slice(0, 10)}`,
    lastModifiedTimestamp: generateRandomTimestamp(),
    createdTimestamp: generateRandomTimestamp(),
    accountFields: createXRandomAccountFields({
      totalCount: 5,
      includeSystemFields: true
    }),
    status: ["InProgress", "Complete"][Math.floor(Math.random() * 2)]!,
    accountsExcluded,
    accountsIncluded,
    totalAccounts,
    tam
  };
};

export const createRandomScoringModel = (): ScoringModelV1 => {
  return {
    id: crypto.randomUUID(),
    workspaceId: useSessionStore().selectedWorkspace()?.id as string,
    name: crypto.randomUUID().split("-")[0]!,
    accountFieldWeights: createXRandomScoreWeights({ totalCount: 7 }),
    distributions: [
      {
        metric: "SCORE",
        scores: createXRandomAccountModelScores({ totalCount: 101 })
      }
    ],
    tiers: createRandomAccountScoreTiers(),
    status: Math.random() < 0.5 ? "IN_PROGRESS" : "COMPLETED",
    lastModifiedTimestamp: generateRandomTimestamp(),
    createdTimestamp: generateRandomTimestamp()
  };
};

export const createXRandomScoringModels = (config: {
  totalCount: number;
}): ScoringModelV1[] => {
  return Array.from(Array(config.totalCount)).map((_, i) =>
    createRandomScoringModel()
  );
};

export const createXRandomWeightedTams = (config: {
  totalCount: number;
}): ScoringModelWeightedTamV1[] => {
  return Array.from(Array(config.totalCount)).map((_, i) => ({
    pricingId: crypto.randomUUID().split("-")[0]!,
    pricingName: crypto.randomUUID().split("-")[0]!,
    weightedTam: Math.floor(Math.random() * (10000000 - 1000000 + 1)) + 1000000
  }));
};

export const createRandomAccountModelScore = (
  score: number
): ScoringModelScoreV1 => ({
  score,
  numberOfAccounts: 10,
  weightedTams: createXRandomWeightedTams({ totalCount: 5 })
});

export const createXRandomAccountModelScores = (config: {
  totalCount: number;
}): ScoringModelScoreV1[] => {
  return Array.from(Array(config.totalCount)).map((_, i) =>
    createRandomAccountModelScore(i / 100)
  );
};

export const createRandomAccountScoreTier = (
  start: number,
  end: number
): Required<ScoringModelV1>["tiers"][number] => {
  return {
    name: crypto.randomUUID().split("-")[0]!,
    start,
    end,
    numberOfAccounts: 100,
    weightedTams: createXRandomWeightedTams({ totalCount: 5 })
  };
};

export const createRandomScoreWeight =
  (): ScoringModelV1["accountFieldWeights"][number] => {
    return {
      accountFieldName: crypto.randomUUID().split("-")[0]!,
      weight: Math.round(Math.random() * 100) / 100,
      valueWeights: []
    };
  };

export const createXRandomScoreWeights = (config: {
  totalCount: number;
}): ScoringModelV1["accountFieldWeights"] => {
  return Array.from(Array(config.totalCount)).map((_, i) =>
    createRandomScoreWeight()
  );
};

export const createRandomAccountScoreTiers = (): ScoringModelV1["tiers"] => {
  return [
    createRandomAccountScoreTier(0.75, 1),
    createRandomAccountScoreTier(0.5, 0.74),
    createRandomAccountScoreTier(0.3, 0.49),
    createRandomAccountScoreTier(0.2, 0.29)
  ];
};

export const createXRandomValueWeights = (config: {
  totalCount: number;
}): ScoringModelV1["accountFieldWeights"][number]["valueWeights"] => {
  const weight = Number((1 / config.totalCount).toFixed(2)); // Equal weight for all objects so they add up to 1
  return Array.from({ length: config.totalCount }).map(() => ({
    accountFieldValue: crypto.randomUUID().split("-")[0]!, // string | number | boolean
    weight
  }));
};

export const createXRandomSuggestedAccountFieldWeights = (config: {
  totalCount: number;
}): ScoringModelV1["accountFieldWeights"] => {
  const weight = Number((1 / config.totalCount).toFixed(2));
  return Array.from({ length: config.totalCount }).map(() => ({
    accountFieldName: crypto.randomUUID().split("-")[0]!,
    weight,
    valueWeights: createXRandomValueWeights({ totalCount: 5 })
  }));
};

export const createRandomPersonField = (config: {
  name?: string;
  isSystem: boolean;
}): PersonFieldV1 => {
  const types = ["CURRENCY", "TEXT", "NUMBER", "BOOLEAN"];
  const sampleValues = [
    ["$10,000,000", "$856,098", "$1,200,300"],
    ["Example 1", "Example 2", "Example 3"],
    ["100", "200", "300"],
    ["True", "False"]
  ];
  const index = Math.floor(Math.random() * types.length);
  return {
    id: crypto.randomUUID(),
    name: crypto.randomUUID().split("-")[0]!,
    isSystem: config.isSystem,
    workspaceId: useSessionStore().selectedWorkspace()?.id as string,
    type: types[index]! as PersonFieldV1["type"],
    sampleValues: sampleValues[index]!,
    averageValue: Math.round(Math.random() * 100) / 100,
    mapToAccountFieldName:
      Math.random() < 0.2
        ? createRandomAccountField({
            isSystem: Math.random() > 0.5,
            name: sample(["Account Type", "Industry", "Account Status"])!
          }).name
        : undefined
  };
};

export const createXRandomPersonFields = (config: {
  totalCount: number;
  includedSystemFields: boolean;
  fieldNames?: string[];
}): PersonFieldV1[] => {
  const systemFields = createMockSystemPersonFields();
  const customFields = Array.from(Array(config.totalCount)).map((_, i) =>
    createRandomPersonField({
      isSystem: false,
      name: config.fieldNames?.[i]
    })
  );
  if (config.includedSystemFields) return systemFields.concat(customFields);
  return customFields;
};

export const createRandomAccount = (): AccountV1 => {
  return {
    id: crypto.randomUUID(),
    lastModifiedTimestamp: generateRandomTimestamp(),
    createdTimestamp: generateRandomTimestamp(),
    workspaceId: useSessionStore().selectedWorkspace()?.id as string,
    "Account ID": crypto.randomUUID(),
    "Account Name": crypto.randomUUID().split("-")[0]!,
    "Account Owner Name": crypto.randomUUID().split("-")[0]!,
    "Account Type": crypto.randomUUID().split("-")[0]!,
    Industry: crypto.randomUUID().split("-")[0]!,
    "Annual Revenue": Math.round(Math.random() * 100000),
    "Number Of Employees": Math.round(Math.random() * 10000),
    City: ["New York", "San Francisco", "Seattle"][
      Math.floor(Math.random() * 3)
    ]!,
    State: ["NY", "CA", "WA", "UT", "TX"][Math.floor(Math.random() * 5)]!,
    Latitude: 40,
    Longitude: -111,
    customFields: [
      { name: "Number Of Locations", value: Math.random() * 100 },
      {
        name: "Sub Industry",
        value: crypto.randomUUID().split("-")[0]!
      },
      { name: "Sub Region", value: crypto.randomUUID().split("-")[0]! },
      {
        name: "Enterprise",
        value: crypto.randomUUID().split("-")[0]!
      }
    ],
    pricingSummaries: Array.from(Array(Math.round(Math.random() * 5))).map(
      (_) => ({
        id: crypto.randomUUID(),
        workspaceId: useSessionStore().selectedWorkspace()?.id as string,
        pricingId: crypto.randomUUID(),
        tam: Math.round(Math.random() * 1000000),
        offerings: Array.from(Array(Math.round(Math.random() * 10))).map(
          (_) => ({
            name: crypto.randomUUID().split("-")[0]!,
            tam: Math.round(Math.random() * 1000000)
          })
        ),
        name: crypto.randomUUID().split("-")[0]!
      })
    ),
    scoringSummaries: Array.from(Array(Math.round(Math.random() * 5))).map(
      (_) => ({
        id: crypto.randomUUID(),
        workspaceId: useSessionStore().selectedWorkspace()?.id as string,
        scoringId: crypto.randomUUID(),
        score: Math.round(Math.random() * 100),
        name: crypto.randomUUID().split("-")[0]!,
        rank: Math.round(Math.random() * 100),
        basis: Math.round(Math.random() * 100),
        totalRankings: Math.round(Math.random() * 100)
      })
    ),
    rankSummaries: Array.from(Array(Math.round(Math.random() * 5))).map(
      (_) => ({
        pricingId: crypto.randomUUID(),
        scoringId: crypto.randomUUID(),
        rank: Math.round(Math.random() * 100),
        weightedTam: Math.round(Math.random() * 1000000),
        basis: Math.round(Math.random() * 100),
        totalRankings: Math.round(Math.random() * 100)
      })
    )
  };
};

export const createXRandomAccounts = (config: {
  totalCount: number;
}): AccountV1[] => {
  return Array.from(Array(config.totalCount)).map((_) => createRandomAccount());
};

export const createRandomPerson = (config?: {
  name?: string;
  employeeId?: string;
  managerEmployeeId?: string;
}): PersonV1 => {
  const randomNum = (min: number, max: number) =>
    Math.floor(Math.random() * (max - min + 1)) + min;

  const randomPersonNames = [
    "John Smith",
    "Jane Doe",
    "Adam Kling",
    "James Wilson",
    'George "The Animal" Steele',
    "Randy Savage",
    "Hulk Hogan",
    "The Rock",
    "Stone Cold Steve Austin",
    "The Undertaker",
    "Kane",
    "Triple H",
    "Shawn Michaels",
    "Bret Hart",
    "Ric Flair",
    "Macho"
  ];

  return {
    id: crypto.randomUUID(),
    workspaceId: useSessionStore().selectedWorkspace()?.id as string,
    employeeId: config?.employeeId ?? crypto.randomUUID().split("-")[0],
    name: config?.name || (sample(randomPersonNames) as string),
    roleName: createRandomRole().name,
    managerEmployeeId: config?.managerEmployeeId,
    indirectManagers: sampleSize(
      randomPersonNames,
      Math.floor(Math.random() * 3)
    ),
    department: sample(["Mid-Market", "Enterprise", "SMB"]),
    location: sample(["North America", "LATAM", "EMEA", "APAC"]),
    customFields: [
      {
        name: "Sales Quota",
        value: randomNum(100000, 500000)
      },
      {
        name: "Team",
        value: sample(["Team 1", "Team 2", "Team 3", "Team 4", "Team 5"])
      },
      {
        name: "Rep",
        value: `R. ${crypto.randomUUID().split("-")[0]!}`
      },
      {
        name: "Sales Acct. Target",
        value: randomNum(50, 100)
      }
    ]
  };
};

export const createXRandomPeople = (count: number): PersonV1[] => {
  return Array.from({ length: count }).map((__, i) => {
    const managerEmployeeId = `EMP-${Math.floor(Math.random() * i) + 1}`;
    return createRandomPerson({
      employeeId: `EMP-${i + 1}`,
      managerEmployeeId:
        i === 0
          ? undefined
          : Math.random() < 0.8
            ? managerEmployeeId
            : undefined
    });
  });
};

export const createRandomRole = (config?: { name?: string }): RoleV1 => {
  const randomRoleNames = [
    "Organization",
    "Admin",
    "Operations",
    "HR",
    "Finance",
    "Legal",
    "IT",
    "Sales",
    "Marketing",
    "Customer Success",
    "Engineering",
    "Product",
    "Support",
    "Data",
    "Design",
    "Research",
    "Content",
    "Growth",
    "Security",
    "Recruiting",
    "Facilities",
    "Other"
  ];
  const isGroup = [true, false][Math.floor(Math.random() * 2)]!;
  return {
    id: crypto.randomUUID(),
    name:
      config?.name ??
      randomRoleNames
        // Remove the first element to avoid the "Organization" role
        .slice(1)
        // choose a random role but avoid direct reports to itself
        .splice(
          Math.floor(Math.random() * (randomRoleNames.length - 1)),
          1
        )[0]!,
    isGroup,
    lastModifiedTimestamp: new Date().getTime(),
    createdTimestamp: new Date().getTime(),
    workspaceId: useSessionStore().selectedWorkspace()?.id as string,

    maxDirectReports: Math.floor(Math.random() * 10) + 5,
    minDirectReports: Math.floor(Math.random() * 10),
    maxIndirectReports: Math.floor(Math.random() * 10) + 5,
    directReports: Array.from(
      new Set(
        Array.from({ length: 5 }).map(() => sample(randomRoleNames) as string)
      )
    ),
    indirectReports: Array.from(
      new Set(
        Array.from({ length: 5 }).map(() => sample(randomRoleNames) as string)
      )
    ),
    annualQuota: isGroup ? undefined : Math.round(Math.random() * 1000000),
    annualQuotaMetric: ["REVENUE", "PIPELINE", "MEETINGS", "LEADS"][
      Math.floor(Math.random() * 4)
    ] as RoleV1["annualQuotaMetric"]
  };
};

export const createXRandomMockRoles = (config: {
  totalCount: number;
}): RoleV1[] => {
  const isGroup = [true, false][Math.floor(Math.random() * 2)]!;
  return Array.from({ length: config.totalCount - 1 })
    .map(() => createRandomRole())
    .concat([
      // assume this is the 'default' role
      {
        id: crypto.randomUUID(),
        name: "Organization",
        isGroup,
        createdTimestamp: new Date().getTime(),
        workspaceId: useSessionStore().selectedWorkspace()?.id as string,
        maxDirectReports: 0,
        minDirectReports: 0,
        maxIndirectReports: 0,
        directReports: ["Admin", "Operations"],
        indirectReports: ["HR", "Finance", "Legal", "IT"],
        annualQuota: isGroup ? undefined : Math.round(Math.random() * 1000000),
        annualQuotaMetric: ["REVENUE", "PIPELINE", "MEETINGS", "LEADS"][
          Math.floor(Math.random() * 4)
        ] as RoleV1["annualQuotaMetric"]
      }
    ]);
};

export const createRandomCapacityModel = (): CapacityModelV1 => {
  const roleNames = ["SDR", "AE", "Enterprise Rep", "Manager"];
  const funnelStageMetrics = ["LEADS", "MEETINGS", "PIPELINE", "REVENUE"];

  // Generate funnel stages first (LEADS has no role)
  const funnelStages = funnelStageMetrics.map((metric, index) => ({
    stage: index + 1,
    name: `${metric.charAt(0) + metric.slice(1).toLowerCase()} Stage`,
    metric: metric as CapacityModelFunnelStageV1["metric"],
    roleName:
      metric === "LEADS"
        ? undefined
        : roleNames[Math.floor(Math.random() * roleNames.length)],
    conversion: Math.random()
  }));

  // Generate role ramp and outputs based on funnel stages with roles
  const roleRampAndOutputs = funnelStages
    .filter((stage) => stage.roleName)
    .reduce((acc, stage) => {
      const existingRole = acc.find((role) => role.roleName === stage.roleName);

      if (existingRole) {
        existingRole.monthlyOutputs.push({
          output: Math.round(Math.random() * 10000),
          metric: stage.metric as "REVENUE" | "PIPELINE" | "MEETINGS"
        });
      } else {
        // Create new role entry
        acc.push({
          roleName: stage.roleName!,
          ramp: Array.from(Array(12), (_, index) => ({
            month: index + 1,
            percentage: Math.random()
          })),
          monthlyOutputs: [
            {
              output: Math.round(Math.random() * 10000),
              metric: stage.metric as "REVENUE" | "PIPELINE" | "MEETINGS"
            }
          ]
        });
      }

      return acc;
    }, [] as CapacityModelRoleRampAndOutputV1[]);

  return {
    id: crypto.randomUUID(),
    status: "COMPLETED",
    workspaceId: useSessionStore().selectedWorkspace()?.id as string,
    name: crypto.randomUUID().split("-")[0]!,
    startDate: new Date().toISOString().split("T")[0]!,
    attainmentDate: new Date().toISOString().split("T")[0]!,
    lastModifiedTimestamp: new Date().getTime(),
    totalRevenueGoal: Math.round(Math.random() * 100000),
    segments: [
      {
        name: crypto.randomUUID().split("-")[0]!,
        revenueGoal: Math.round(Math.random() * 100000),
        personFields: Array.from(
          { length: Math.floor(Math.random() * 5) + 1 },
          () => ({
            name: crypto.randomUUID().split("-")[0]!,
            value: crypto.randomUUID().split("-")[0]!
          })
        ),
        roleRampAndOutputs,
        averageDealSize: Math.round(Math.random() * 50000),
        funnelStages
      }
    ],

    averageDealSize: Math.round(Math.random() * 50000)
  };
};

export const createXRandomCapacityModels = (config: {
  totalCount: number;
}): CapacityModelV1[] => {
  return Array.from(Array(config.totalCount)).map((_) =>
    createRandomCapacityModel()
  );
};

export const createRandomJob = (): JobV1 => {
  return {
    id: crypto.randomUUID(),
    workspaceId: useSessionStore().selectedWorkspace()?.id as string,
    status: ["NEW", "IN_PROGRESS", "ERROR", "COMPLETED"][
      Math.floor(Math.random() * 4)
    ] as JobV1["status"],
    description: "Mock job description",
    lastModifiedTimestamp: new Date().getTime(),
    createdTimestamp: new Date().getTime(),
    type: [
      "FILE_ACCOUNT_SUGGESTED_FIELD_MAP",
      "FILE_PEOPLE_SUGGESTED_FIELD_MAP",
      "CONNECTOR_SUGGESTED_FIELD_MAP",
      "ACCOUNT_CONFIRMED_FIELD_MAP",
      "ACCOUNT_FIELD",
      "ACCOUNT_EXPORT",
      "PRICING",
      "SCORING_SUGGESTED_ACCOUNT_FIELD_WEIGHTS",
      "SCORING"
    ][Math.floor(Math.random() * 7)] as JobV1["type"]
  };
};

export const createXRandomJobs = (config: { totalCount: number }): JobV1[] => {
  return Array.from(Array(config.totalCount)).map((_) => createRandomJob());
};

export const createRandomPricingOffering = (): PricingModelOfferingV1 => {
  return {
    name: `Product ${crypto.randomUUID().split("-")[0]!}`,
    price: Math.random() * 1000,
    currency: "USD",
    numberOfAccounts: Math.floor(Math.random() * 1000),
    tam: Math.floor(Math.random() * 100000),
    billingFrequency: sample(["ONCE", "MONTHLY", "YEARLY"]),
    matchCriteria: [
      {
        accountFieldName: crypto.randomUUID().split("-")[0]!,
        accountFieldOperator: sample([
          "EQUALS",
          "NOT_EQUAL",
          "GREATER_THAN",
          "LESS_THAN",
          "GREATER_THAN_EQUAL",
          "LESS_THAN_EQUAL"
        ]),
        accountFieldValue: 10000,
        accountFieldDefaultValue: sample([
          "MOST_COMMON",
          "AVERAGE",
          "MAX",
          "MIN",
          "IGNORE",
          "CUSTOM"
        ])
      }
    ]
  };
};

export const createXRandomPricingOfferings = (config: {
  totalCount: number;
}) => {
  return Array.from(Array(config.totalCount)).map((_) =>
    createRandomPricingOffering()
  );
};

export const createRandomPricingModel = (): PricingModelV1 => {
  const offerings = createXRandomPricingOfferings({
    totalCount: Math.floor(Math.random() * 15) + 1
  });
  const tam = offerings.reduce((acc, offering) => acc + (offering.tam || 0), 0);
  return {
    id: crypto.randomUUID(),
    status: ["NEW", "IN_PROGRESS", "COMPLETED"][
      Math.floor(Math.random() * 3)
    ] as PricingModelV1["status"],
    tam,
    name: `Pricing ${crypto.randomUUID().split("-")[0]!}`,
    lastModifiedTimestamp: generateRandomTimestamp(),
    createdTimestamp: generateRandomTimestamp(),
    workspaceId: "C56A4180-65AA-42EC-A945-5FD21DEC0538",
    numberOfAccounts: Math.floor(Math.random() * 10000),
    offerings: createXRandomPricingOfferings({
      totalCount: Math.floor(Math.random() * 15) + 1
    })
  };
};

export const createXRandomPricingModels = (config: {
  totalCount: number;
}): PricingModelV1[] => {
  return Array.from(Array(config.totalCount)).map((_) =>
    createRandomPricingModel()
  );
};

export const createRandomWorkspace = (): WorkspaceV1 => {
  return {
    id: crypto.randomUUID(),
    name: `Workspace ${crypto.randomUUID().split("-")[0]!}`,
    lastModifiedTimestamp: generateRandomTimestamp(),
    createdTimestamp: generateRandomTimestamp(),
    credits: Math.floor(Math.random() * 10000),
    currentBillingPlanId: crypto.randomUUID(),
    billingStartDate: generateRandomTimestamp().toString(),
    billingRenewalDate: generateRandomTimestamp().toString()
  };
};

export const createRandomWorkspaceUserGroup = (): WorkspaceUserGroupV1 => {
  return {
    id: crypto.randomUUID(),
    group: ["OWNER", "ADMIN", "USER"][
      Math.floor(Math.random() * 3)
    ] as WorkspaceUserGroupV1["group"],
    lastModifiedTimestamp: generateRandomTimestamp(),
    createdTimestamp: generateRandomTimestamp(),
    workspaceId: crypto.randomUUID(),
    userId: crypto.randomUUID()
  };
};

export const createRandomUser = (): UserV1 => {
  return {
    id: crypto.randomUUID(),
    givenName: crypto.randomUUID().split("-")[0]!,
    familyName: crypto.randomUUID().split("-")[0]!,
    email: `${crypto.randomUUID().split("-")[0]!}@example.com`,
    isEmailVerified: [true, false][Math.floor(Math.random() * 2)],
    lastModifiedTimestamp: generateRandomTimestamp(),
    createdTimestamp: generateRandomTimestamp(),
    lastSignInTimestamp: generateRandomTimestamp(),
    workspaceUserGroups: [createRandomWorkspaceUserGroup()]
  };
};

export const createXRandomUsers = (config: {
  totalCount: number;
}): UserV1[] => {
  return Array.from(Array(config.totalCount)).map((_) => createRandomUser());
};

export const createRandomBillingPlan = (config: {
  planGroupName?: string;
  planNames: string[];
  price?: number;
  visible?: boolean;
  groupId?: string;
  id?: string;
}): BillingPlanV1 => {
  const groupId = config.groupId ?? crypto.randomUUID();
  return {
    id: config.id || crypto.randomUUID(),
    name: config.planGroupName || `Plan ${crypto.randomUUID().split("-")[0]!}`,
    price: config.price ?? Math.floor(Math.random() * 10000),
    credits: Math.floor(Math.random() * 10000),
    paymentTerm: sample(["MONTHLY", "ANNUALLY"]),
    visible: !!config.visible,
    contractTerm: "ANNUAL",
    currency: "USD",
    features: [],
    billingPlanGroupId: groupId,
    billingPlanGroup: {
      id: groupId,
      name: `Group ${crypto.randomUUID().split("-")[0]!}`,
      description: `Description ${crypto.randomUUID().split("-")[0]!}`,
      marketingFeatures: ["Feature 1", "Feature 2", "Feature 3"]
    },
    sortOrder: Math.floor(Math.random() * 100),
    lastModifiedTimestamp: generateRandomTimestamp(),
    createdTimestamp: generateRandomTimestamp()
  };
};

export const createRandomBillingPayment = (): BillingPaymentV1 => {
  return {
    id: crypto.randomUUID(),
    workspaceId: crypto.randomUUID(),
    amount: Math.floor(Math.random() * 10000),
    timestamp: generateRandomTimestamp(),
    currency: "USD",
    method: ["CARD", "INVOICE", "BANK_TRANSFER"][
      Math.floor(Math.random() * 3)
    ] as BillingPaymentV1["method"],
    status: ["PENDING", "COMPLETED", "FAILED"][
      Math.floor(Math.random() * 3)
    ] as BillingPaymentV1["status"],
    lastModifiedTimestamp: generateRandomTimestamp(),
    createdTimestamp: generateRandomTimestamp()
  };
};

export const createXRandomBillingPayments = (config: {
  totalCount: number;
}): BillingPaymentV1[] => {
  return Array.from(Array(config.totalCount)).map((_, i) =>
    createRandomBillingPayment()
  );
};

export const createRandomCreditLog = (): CreditLogV1 => {
  return {
    id: crypto.randomUUID(),
    workspaceId: crypto.randomUUID(),
    credits: Math.floor(Math.random() * 10000),
    balance: Math.floor(Math.random() * 10000),
    category: [
      "PAYMENT",
      "ACCOUNTS",
      "SCORING",
      "PRICING",
      "PEOPLE",
      "ROLES",
      "CAPACITY",
      "TERRITORY"
    ][Math.floor(Math.random() * 8)]!,
    timestamp: generateRandomTimestamp(),
    lastModifiedTimestamp: generateRandomTimestamp(),
    createdTimestamp: generateRandomTimestamp()
  };
};

export const createXRandomCreditLogs = (config: {
  totalCount: number;
}): CreditLogV1[] => {
  return Array.from(Array(config.totalCount)).map((_, i) =>
    createRandomCreditLog()
  );
};

export const createRandomTerritoryModel = (): TerritoryModelV1 => {
  return {
    id: crypto.randomUUID(),
    name: crypto.randomUUID().split("-")[0]!,
    active: false,
    description: crypto.randomUUID(),
    lastModifiedTimestamp: generateRandomTimestamp(),
    createdTimestamp: generateRandomTimestamp(),
    workspaceId: crypto.randomUUID(),
    scoringId: crypto.randomUUID(),
    segments: [
      {
        name: crypto.randomUUID().split("-")[0]!,
        distributionCriteria: [
          {
            roleName: crypto.randomUUID().split("-")[0]!,
            filters: [
              {
                name: "TIER",
                operator: "OR",
                rules: [
                  {
                    comparison: "=",
                    value: crypto.randomUUID().split("-")[0]!
                  }
                ]
              }
            ]
          }
        ]
      }
    ],
    pricingId: crypto.randomUUID(),
    capacityId: crypto.randomUUID(),
    status: Math.random() < 0.5 ? "IN_PROGRESS" : "COMPLETED"
  };
};

export const createXRandomTerritoryModels = (config: {
  totalCount: number;
}) => {
  return Array.from(Array(config.totalCount)).map((_, i) =>
    createRandomTerritoryModel()
  );
};

export const createRandomTerritory = (): TerritoryV1 => {
  return {
    id: crypto.randomUUID(),
    name: crypto.randomUUID().split("-")[0]!,
    description: crypto.randomUUID(),
    territoryModelId: crypto.randomUUID(),
    lastModifiedTimestamp: generateRandomTimestamp(),
    createdTimestamp: generateRandomTimestamp(),
    workspaceId: crypto.randomUUID()
  };
};

export const createXRandomTerritories = (config: { totalCount: number }) => {
  return Array.from(Array(config.totalCount)).map(() =>
    createRandomTerritory()
  );
};

export const createRandomList = (): ListV1 => {
  return {
    id: crypto.randomUUID(),
    name: crypto.randomUUID().split("-")[0]!,
    description: crypto.randomUUID(),
    lastModifiedTimestamp: generateRandomTimestamp(),
    createdTimestamp: generateRandomTimestamp(),
    workspaceId: crypto.randomUUID()
  };
};

export const createXRandomLists = (config: { totalCount: number }) => {
  return Array.from(Array(config.totalCount)).map(() => createRandomList());
};

export const createRandomAccountList = (): AccountListV1 => {
  return {
    id: crypto.randomUUID(),
    workspaceId: crypto.randomUUID(),
    lastModifiedTimestamp: generateRandomTimestamp(),
    createdTimestamp: generateRandomTimestamp(),
    listId: crypto.randomUUID(),
    accountId: crypto.randomUUID()
  };
};

export const createXRandomAccountLists = (config: { totalCount: number }) => {
  return Array.from(Array(config.totalCount)).map(() =>
    createRandomAccountList()
  );
};

export const createRandomConnector = (): ConnectorV1 => {
  return {
    id: crypto.randomUUID(),
    workspaceId: crypto.randomUUID(),
    name: crypto.randomUUID().split("-")[0]!,
    provider: sample(["GOOGLE", "MICROSOFT", "SALESFORCE", "HUBSPOT"]),
    email: `${crypto.randomUUID().split("-")[0]!}@example.com`,
    accessToken: crypto.randomUUID().split("-")[0]!,
    accessTokenExpiryTimestamp: generateRandomTimestamp(),
    refreshToken: crypto.randomUUID().split("-")[0]!,
    refreshTokenExpiryTimestamp: generateRandomTimestamp(),
    createdUserId: crypto.randomUUID(),
    createdTimestamp: generateRandomTimestamp()
  };
};

export const createXRandomConnectors = (config: { totalCount: number }) => {
  return Array.from(Array(config.totalCount)).map(() =>
    createRandomConnector()
  );
};

export const createRandomTerritoryAccount = (): TerritoryAccountV1 => {
  return {
    id: crypto.randomUUID(),
    territoryId: crypto.randomUUID(),
    workspaceId: crypto.randomUUID(),
    reason: "Mock Reason",
    territoryModelId: crypto.randomUUID(),
    accountId: crypto.randomUUID()
  };
};

export const createXRandomTerritoryAccounts = (config: {
  totalCount: number;
}) => {
  return Array.from(Array(config.totalCount)).map(() =>
    createRandomTerritoryAccount()
  );
};
